/**
 * Service Supabase pour user_inventory
 */

import { supabase } from '@/integrations/supabase/client';
import { UserGamificationInventoryItem } from '@/types/gamificationProducts';
import { logger } from './logger';

const mapRowToInventoryItem = (row: any): UserGamificationInventoryItem => ({
  id: row.id,
  userId: row.user_id || '',
  productTemplateId: row.product_template_id || row.reward_id, // Utilise product_template_id en priorité
  organizationId: row.organization_id || '',
  quantity: row.quantity,
  purchasedAt: row.unlocked_at,
  isActivated: row.activated_at !== null,
  isConsumed: row.status === 'used',
  activatedAt: row.activated_at,
  consumedAt: row.status === 'used' ? row.updated_at : undefined,
  expiresAt: row.expires_at,
  purchasePrice: ((row.metadata as any)?.purchasePrice) || 0,
  source: ((row.metadata as any)?.source) || 'shop'
});

export const getUserInventory = async (userId: string): Promise<UserGamificationInventoryItem[]> => {
  try {
    const { data, error } = await supabase
      .from('user_inventory')
      .select('*')
      .eq('user_id', userId)
      .neq('status', 'used');

    if (error) throw error;

    return (data || []).map(mapRowToInventoryItem);
  } catch (error) {
    logger.error('[Inventory] Error loading user inventory:', error);
    return [];
  }
};

export const addToInventory = async (
  userId: string,
  productTemplateId: string,
  organizationId: string,
  quantity: number = 1,
  purchasePrice: number,
  source: UserGamificationInventoryItem['source'] = 'shop'
): Promise<UserGamificationInventoryItem> => {
  // Déterminer le nom du produit basé sur le template ID
  const itemName = productTemplateId.startsWith('theme-') 
    ? 'Dashboard Theme' 
    : productTemplateId.startsWith('freeze-') 
      ? 'Streak Freeze' 
      : productTemplateId.startsWith('boost-')
        ? 'Boost Multiplier'
        : 'Gamification Product';

  const itemCategory = productTemplateId.startsWith('theme-') 
    ? 'theme' 
    : productTemplateId.startsWith('freeze-') 
      ? 'freeze' 
      : productTemplateId.startsWith('boost-')
        ? 'boost'
        : 'other';

  logger.debug('[Inventory] Adding to inventory:', {
    userId,
    productTemplateId,
    organizationId,
    quantity,
    purchasePrice,
    itemName,
    itemCategory
  });

  const { data, error } = await supabase
    .from('user_inventory')
    .insert({
      user_id: userId,
      product_template_id: productTemplateId, // ID du template gamification
      organization_id: organizationId,
      item_name: itemName,
      item_type: 'gamification',
      item_category: itemCategory,
      quantity,
      status: 'unlocked',
      metadata: {
        purchasePrice,
        source,
        productTemplateId
      }
    } as any) // Cast to any because reward_id is now nullable but types not updated yet
    .select()
    .single();

  if (error) {
    logger.error('[Inventory] Error adding to inventory:', error);
    throw error;
  }

  logger.debug('[Inventory] ✅ Successfully added to inventory:', data);
  return mapRowToInventoryItem(data);
};

export const consumeItem = async (itemId: string): Promise<UserGamificationInventoryItem | null> => {
  const { data, error } = await supabase
    .from('user_inventory')
    .update({
      status: 'used',
      activated_at: new Date().toISOString()
    })
    .eq('id', itemId)
    .select()
    .maybeSingle();

  if (error) {
    logger.error('[Inventory] Error consuming item:', error);
    return null;
  }

  if (!data) {
    logger.warn('[Inventory] Item not found for consumption:', itemId);
    return null;
  }

  return mapRowToInventoryItem(data);
};

export const activateItem = async (itemId: string, expiresAt?: string): Promise<UserGamificationInventoryItem | null> => {
  const { data, error } = await supabase
    .from('user_inventory')
    .update({
      status: 'active',
      activated_at: new Date().toISOString(),
      ...(expiresAt && { expires_at: expiresAt })
    })
    .eq('id', itemId)
    .select()
    .maybeSingle();

  if (error) {
    logger.error('[Inventory] Error activating item:', error);
    return null;
  }

  if (!data) {
    logger.warn('[Inventory] Item not found for activation:', itemId);
    return null;
  }

  return mapRowToInventoryItem(data);
};

export const getItemCount = async (userId: string, productTemplateId: string): Promise<number> => {
  const inventory = await getUserInventory(userId);
  return inventory
    .filter(item => item.productTemplateId === productTemplateId)
    .reduce((sum, item) => sum + item.quantity, 0);
};

export const getAvailableItemsByTemplate = async (
  userId: string,
  productTemplateId: string
): Promise<UserGamificationInventoryItem[]> => {
  const inventory = await getUserInventory(userId);
  return inventory.filter(item => 
    item.productTemplateId === productTemplateId && 
    !item.isConsumed && 
    item.quantity > 0
  );
};

export const getItemById = async (itemId: string): Promise<UserGamificationInventoryItem | null> => {
  const { data, error } = await supabase
    .from('user_inventory')
    .select('*')
    .eq('id', itemId)
    .maybeSingle();

  if (error || !data) return null;

  return mapRowToInventoryItem(data);
};

export const removeFromInventory = async (itemId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('user_inventory')
    .delete()
    .eq('id', itemId);

  return !error;
};

export const markItemAsActive = async (itemId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('user_inventory')
    .update({
      status: 'active',
      activated_at: new Date().toISOString()
    })
    .eq('id', itemId);

  return !error;
};

export const markItemAsInactive = async (itemId: string): Promise<boolean> => {
  const { error } = await supabase
    .from('user_inventory')
    .update({
      status: 'unlocked'
    })
    .eq('id', itemId);

  return !error;
};

/**
 * Consomme UNE unité d'un item (pour les gels de série par quantité)
 * Décrémente la quantité et marque comme 'used' quand épuisé
 * Protection contre les quantités négatives
 */
export interface ConsumeOneResult {
  success: boolean;
  remainingQuantity: number;
  error?: string;
  item?: UserGamificationInventoryItem;
}

/**
 * Consume one unit from an inventory item atomically (Bug #10 fix)
 * Uses RPC to prevent race conditions where multiple requests
 * could read the same quantity and cause incorrect decrements
 */
export const consumeOneFromItem = async (itemId: string): Promise<ConsumeOneResult> => {
  try {
    // Get the user ID from the item first (needed for RPC)
    const { data: item, error: fetchError } = await supabase
      .from('user_inventory')
      .select('user_id')
      .eq('id', itemId)
      .maybeSingle();

    if (fetchError || !item) {
      logger.error('[Inventory] Item not found:', itemId);
      return { success: false, remainingQuantity: 0, error: 'Item introuvable' };
    }

    // Use atomic RPC for consumption
    const { data, error } = await supabase.rpc('consume_inventory_item_atomic', {
      p_item_id: itemId,
      p_user_id: item.user_id
    });

    if (error) {
      logger.error('[Inventory] Atomic consumption failed:', error);
      return { success: false, remainingQuantity: 0, error: 'Erreur lors de la consommation' };
    }

    const result = data as { success: boolean; remaining_quantity: number; error?: string; new_status?: string };

    if (!result.success) {
      logger.warn('[Inventory] Cannot consume - atomic check failed:', { itemId, error: result.error });
      return { 
        success: false, 
        remainingQuantity: 0, 
        error: result.error || 'Stock épuisé' 
      };
    }

    logger.debug('[Inventory] ✅ Successfully consumed 1 item atomically:', {
      itemId,
      remainingQuantity: result.remaining_quantity,
      newStatus: result.new_status
    });

    return { 
      success: true, 
      remainingQuantity: result.remaining_quantity
    };
  } catch (error) {
    logger.error('[Inventory] Unexpected error in consumeOneFromItem:', error);
    return { success: false, remainingQuantity: 0, error: 'Erreur inattendue' };
  }
};

/**
 * Récupère le nombre total de gels disponibles (toutes variantes)
 */
export const getTotalGelCount = async (userId: string): Promise<number> => {
  const freezeTemplateIds = ['freeze-single', 'freeze-duo', 'freeze-trio', 'freeze-basic', 'freeze-premium', 'freeze-ultimate'];
  const inventory = await getUserInventory(userId);
  
  return inventory
    .filter(item => 
      freezeTemplateIds.includes(item.productTemplateId) && 
      !item.isConsumed && 
      item.quantity > 0
    )
    .reduce((sum, item) => sum + item.quantity, 0);
};

/**
 * Récupère tous les items avec des gels disponibles
 */
export const getAvailableGelItems = async (userId: string): Promise<UserGamificationInventoryItem[]> => {
  const freezeTemplateIds = ['freeze-single', 'freeze-duo', 'freeze-trio', 'freeze-basic', 'freeze-premium', 'freeze-ultimate'];
  const inventory = await getUserInventory(userId);
  
  return inventory.filter(item => 
    freezeTemplateIds.includes(item.productTemplateId) && 
    !item.isConsumed && 
    item.quantity > 0
  );
};
